<template>
<div class="common-flexbox flex-content-left-align flex callConfirmationContainer">
    <div class="instruction-heading" v-if="isParticipantsAccessNotRevoked" id="conferenceCallStatus">{{callStatus}}</div>

    <div v-if="isParticipantAccessRevoked">
        <div class="common-flexbox flex-content-left-align" direction="column">
            <div class="new-heading">
                <img class="reg-icon revoke-icon" :src="revokedAccessIcon" />
                {{ $t('call_confirmation.access_revoked') }}
            </div>
            <div class="new-content">{{ $t(getparticipantRevokedMessage) }}&nbsp;{{$t('call_confirmation.access_revoke_msg')}}</div>
        </div>
    </div>

    <div v-else-if="showPublicParticipantAccessRevoked">
        <div class="common-flexbox flex-content-left-align" direction="column">
            <div class="new-heading">
                <img class="reg-icon revoke-icon" />
                {{ $t('call_confirmation.public_access_complete') }}
            </div>
            <div class="new-content">{{ $t(getparticipantRevokedMessage) }}</div>
        </div>
    </div>

    <div class="container" v-else-if="showStreamAndDialInInformation">
        <div class="sub-container">
            <div class="content custom-content" :class="!hasConferenceCallStarted ? 'increase-height' : ''" direction="column">

                <div id="VideoWebinarConfirmationPage">
                    <img class="registered-image" :src="registeredCheck" />
                    <h1 class="videoWebinarHeader">
                        Thank you for registering!
                    </h1>
                    <p class="videoWebinarDetails">
                        You have successfully registered for the {{conferenceCallDetails.PresTitle}} Live Event. Please join using the link or dial-in info provided below (also included in the confirmation email).
                    </p>
                    <button class="enter-event-button" id="enterEventButton" @click="enterEventClick()">
                        Enter Event
                    </button>
                    <div v-if="!hideDialInDetails" id="callConfirmationDialInDetails">
                        <p>
                            OR Dial-In
                        </p>
                        <div v-if="dialInDetails.AutomatedPhoneNumbers || dialInDetails.AutomatedTollPhoneNumbers">
                            <div v-if="dialInDetails.AutomatedTollPhoneNumbers.length" class="common-flexbox dial-in-information flex-wrap-mobile">
                                <span class="custom-label" id="tollCountryName">{{dialInDetails.Country.Name}}:</span>
                                <span class="custom-value number">
                                    <a id="tollNumber" :href="getFormattedLink(dialInDetails.AutomatedTollPhoneNumbers[0], dialInDetails.Country.Code, false)">{{telFormatter(dialInDetails.AutomatedTollPhoneNumbers[0], dialInDetails.Country.Code)}}</a>
                                </span>
                            </div>
                            <div v-if="dialInDetails.AutomatedPhoneNumbers.length" class="common-flexbox dial-in-information flex-wrap-mobile">
                                <span class="custom-label" id="tollFreeCountryName">{{dialInDetails.Country.Name}} Toll-Free:</span>
                                <span class="custom-value number">
                                    <a id="tollFreeNumber" :href="getFormattedLink(dialInDetails.AutomatedPhoneNumbers[0], dialInDetails.Country.Code, false)">{{telFormatter(dialInDetails.AutomatedPhoneNumbers[0], dialInDetails.Country.Code)}}</a>
                                </span>
                            </div>
                        </div>
                        <div class="common-flexbox flex-content-center-align dial-in-information">
                            <span class="custom-label" id="accessCodeLabel">{{ $t('call_confirmation.access_code') }}</span>
                            <span class="custom-value" id="accessCodeValue">{{accessCodeFormat(automatedEventId, conferenceCallDetails.BridgeType)}}</span>
                        </div>

                        <div class="common-flexbox flex-content-center-align dial-in-information">
                            <span class="custom-label" id="pinCodeLabel">{{ $t('call_confirmation.your_PIN') }}</span>
                            <span class="custom-value" id="pinCodeValue">{{accessCodeFormat(accessCodes.pin, conferenceCallDetails.BridgeType)}}</span>
                        </div>

                        <div class="common-flexbox flex-content-center-align dial-in-information">
                            <img class="global-dial-in-image" :src="globeIcon" />
                            <div class="global-dial-in-link" id="globalDialInNumberLink" @click="toggleModalWindow(true)">{{ $t('call_confirmation.global_dial_in_numbers') }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <GlobalNumbersModal :show="showModalWindow" :onClose="toggleModalWindow" :numbers="globalNumbers" :eventId="automatedEventId" :pin="accessCodes.pin" :pageTitle="conferenceCallDetails.CallTitle || conferenceCallDetails.PresTitle" :bridgeType="conferenceCallDetails.BridgeType"></GlobalNumbersModal>
</div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeMount, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute, onBeforeRouteLeave as useBeforeRouteLeave } from 'vue-router';

import TelFormatter from "@/service/telFormatter";
import revokedAccessIcon from '@/assets/icons/lock.svg';
import calendar from '@/assets/icons/calendar_actual.svg';
import headset from '@/assets/icons/headset-Filled.svg';
import headsetWhite from '@/assets/icons/headset-white.svg';
import phone from '@/assets/icons/phone-filled.svg';
import phoneWhite from '@/assets/icons/phone-white.svg';
import globeIcon from '@/assets/icons/icons8-globe.svg';
import streamMuteOn from '@/assets/icons/mute_on.svg';
import streamMuteOff from '@/assets/icons/mute_off.svg';
import loadingState from '@/assets/icons/icons8-clock.svg'
import streamNotStarted from '@/assets/icons/icons8-important_time.svg'
import connectionError from '@/assets/icons/icons8-error.svg'
import registeredCheck from '@/assets/icons/icons8-checked_user_male.svg';
import GlobalNumbersModal from "@/components/common/GlobalNumbersModal/";
import Constants from "@/utils/constants";
import moment from "moment";
import {
    changeRoute,
    changeRouteWithUniqueId,
    accessCodeFormatter,
    isWebBasedReplayEnabled,
    isPhoneBasedReplayEnabled
} from "@/utils/helper";
import {
    getAttendeeUrl
} from "@/service/api";
import {
    redirectToPresentationViewer
} from '@/service/routerService';

// Import composables
import {
    useConferenceCall,
    usePresentation,
    useGlobalNumbers,
    useLogin,
    useGlobal,
    useService,
    useCommonMixin
} from '@/composables';

// Define component options
defineOptions({
    name: 'callConfirmation'
});

// Composables
const { t } = useI18n();
const router = useRouter();
const route = useRoute();

// Initialize composables
const {
    conferenceCallDetails,
    conferenceCallStatus,
    dateTimeNow,
    callStatus,
    hasConferenceCallStarted,
    participants,
    userStatus,
    isParticipantAccessRevoked,
    uniqueId,
    fetchDateTimeNow,
    fetchConferenceCallStatus,
    stopUserStatusPolling,
    stopConferenceCallStatusPolling,
    startParticipantPolling,
    stopParticipantPolling,
    resetConferenceStatus,
    updateMetadata,
    isEndedStatus,
    isExpiredStatus
} = useConferenceCall();

const {
    accessCodes,
    presentation,
    fetchAccessCodes
} = usePresentation();

const {
    globalNumbers,
    fetchGlobalNumbers
} = useGlobalNumbers();

const {
    loggedInUser
} = useLogin();

const {
    displaySystemMessage,
    displaySystemErrorMessage
} = useGlobal();

const {
    setSystemMessageTimeout
} = useService();

// Common mixin functionality
const {
    calculateDateTimeOffsetAndPoll,
    onStatusChanged,
    findPhoneNumberObjectByCountry
} = useCommonMixin();

// Reactive data
const conferenceStatus = ref(Constants.ConferenceStatus);
const showModalWindow = ref(false);
const dialInDetails = ref({});

// Icon constants (previously in data)
const icons = {
    revokedAccessIcon,
    headsetWhite,
    headset,
    phone,
    phoneWhite,
    calendar,
    globeIcon,
    streamMuteOn,
    streamMuteOff,
    loadingState,
    streamNotStarted,
    connectionError,
    registeredCheck
};

// Lifecycle: created() equivalent
const initializeComponent = async () => {
    fetchDateTimeNow();
    if (conferenceCallDetails.value.Status === Constants.ConferenceStatus.Ended) {
        fetchConferenceCallStatus();
    } else {
        await setDialInDetails();
    }
    if (sessionStorage.getItem(Constants.SessionStorageKeys.AccountConfirmed)) {
        sessionStorage.removeItem(Constants.SessionStorageKeys.AccountConfirmed);
        displaySystemMessage(Constants.Messages.AccountConfirmation);
    }
    if (sessionStorage.getItem(Constants.SessionStorageKeys.RegistrationSuccessful)) {
        sessionStorage.removeItem(Constants.SessionStorageKeys.RegistrationSuccessful);
        if (!isParticipantAccessRevoked.value) {
            if (showedConferencePostRegistrationAlert.value || conferenceCallDetails.value.Status !== Constants.ConferenceStatus.NotStarted) {
                displaySystemMessage(
                    t(
                        Constants.Messages.RegistrationConfirmation, {
                            presTitle: conferenceCallDetails.value.CallTitle || conferenceCallDetails.value.PresTitle,
                            emailAddress: loggedInUser.value.EmailAddress
                        },
                    )
                );
            }
        }
    }

    if (!isParticipantAccessRevoked.value) {
        startParticipantPolling();
    }
};
// Computed properties
const isParticipantsAccessNotRevoked = computed(() => {
    return !isParticipantAccessRevoked.value && !showPublicParticipantAccessRevoked.value;
});

const isReplayEnabledAndStatusEnded = computed(() => {
    return isWebBasedReplayEnabled(conferenceCallDetails.value.ReplayType) && (isEndedStatus.value || isExpiredStatus.value);
});

const showStreamAndDialInInformation = computed(() => {
    return isVideoWebinarEvent.value && isParticipantsAccessNotRevoked.value && !isReplayEnabledAndStatusEnded.value;
});

const redirectVWToPresentationViewer = computed(() => {
    return isVideoWebinarEvent.value && isParticipantsAccessNotRevoked.value && isReplayEnabledAndStatusEnded.value;
});

const selectedLang = computed(() => {
    const { locale } = useI18n();
    return locale.value;
});

const hideDialInDetails = computed(() => {
    return isVideoWebinarEvent.value && participants.value.RoleType === Constants.ParticipantRoleType.Speaker;
});

const showPublicParticipantAccessRevoked = computed(() => {
    return (
        conferenceCallDetails.value.PublicCallEndTimeUtc &&
        participants.value.IsPublicParticipant &&
        (!isEndedStatus.value && !isExpiredStatus.value)
    );
});

const showedConferencePostRegistrationAlert = computed(() => {
    return loggedInUser.value.ShowedConferencePostRegistrationAlert;
});

const automatedEventId = computed(() => {
    return accessCodes.value.eventIds[Constants.EventIdTypes.Automated] ? accessCodes.value.eventIds[Constants.EventIdTypes.Automated] : '';
});

const operatorAssistedEventId = computed(() => {
    return accessCodes.value.eventIds[Constants.EventIdTypes.OperatorAssisted] ? accessCodes.value.eventIds[Constants.EventIdTypes.OperatorAssisted] : '';
});

const getparticipantRevokedMessage = computed(() => {
    return Constants.ErrorMessages.AccessRevokedMessageForNoVideo;
});

const isVideoWebinarEvent = computed(() => {
    return conferenceCallDetails.value.ShowType == Constants.ShowTypes.videoWebinar;
});
// Methods
const getFormattedLink = (tel, countryCode, hideTelephoneCode) => {
    const formattedNumber = TelFormatter(tel, countryCode, hideTelephoneCode);
    const hash = conferenceCallDetails.value.BridgeType === Constants.BridgeTypes.Compunetix ? '%23' : '';
    return `tel:${formattedNumber}%2C%2C${automatedEventId.value}${hash}%2C%2C${accessCodes.value.pin}${hash}`;
};

const toggleModalWindow = (show) => {
    showModalWindow.value = show;
};

const getUserDialInDetailsBasedOnCountry = (phoneNumbers) => {
    if (conferenceCallDetails.value.ClientProfile === null) {
        return;
    }
    let defaultCountryId = conferenceCallDetails.value.ClientProfile.CountryDetails.Id;
    let userPhoneNumberDetails = findPhoneNumberObjectByCountry(
        phoneNumbers,
        defaultCountryId
    );
    return userPhoneNumberDetails ? userPhoneNumberDetails : {};
};

const setDialInDetails = async () => {
    await fetchGlobalNumbers(conferenceCallDetails.value.Id);
    dialInDetails.value = getUserDialInDetailsBasedOnCountry(
        globalNumbers.value
    );
};

const enterEventClick = async () => {
    var start = conferenceCallDetails.value.StartTimeUtc;
    var current = new Date().toISOString();
    var newStart = moment(start).subtract(Constants.VideoWebinarPriorJoinTime.Minutes, 'minutes').utc().format();
    setSystemMessageTimeout(10000);

    if (current >= newStart) {
        getAttendeeUrl(conferenceCallDetails.value.PresId).then(response => {
            var attendeeUrl = response.data;
            attendeeUrl = attendeeUrl.replaceAll('"', '');
            window.location.href = attendeeUrl;
        }, error => {
            displaySystemErrorMessage(error);
        });
    } else {
        displaySystemMessage(
            t(
                Constants.Messages.EventNotAvailable, {
                    minutes: Constants.VideoWebinarPriorJoinTime.Minutes
                },
            ));
    }
};

const onAccessRevoked = () => {
    stopParticipantPolling();
    stopUserStatusPolling();
};

const onUserStatusChange = (userStatusValue) => {
    if (!conferenceCallDetails.value.EnableLiveStream) {
        if (userStatusValue === Constants.UserStatus.Connected) {
            stopParticipantPolling();
        } else if (
            userStatusValue !== Constants.UserStatus.Connected &&
            !isParticipantAccessRevoked.value
        ) {
            startParticipantPolling();
        }
    }
};

const onConferenceStatusUpdate = (conferenceStatusValue) => {
    if (conferenceStatusValue.Status === conferenceStatus.value.Expired) {
        if(uniqueId.value){
            changeRouteWithUniqueId.call({ $router: router }, {
                name: "conference-ended-new"
            }, uniqueId.value);
        } else {
            changeRoute.call({ $router: router }, {
                name: "conference-ended"
            });
        }
    }
    if (conferenceStatusValue.Status === conferenceStatus.value.Ended) {
        stopUserStatusPolling();
        stopParticipantPolling();
    }
};

const telFormatter = (tel, countryCode, hideTelephoneCode) => {
    return TelFormatter(tel, countryCode, hideTelephoneCode);
};

const accessCodeFormat = (value, bridgeType) => {
    return accessCodeFormatter(value, bridgeType);
};
// Watchers
watch(selectedLang, (newVal, oldVal) => {
    if (newVal != oldVal) {
        calculateDateTimeOffsetAndPoll(
            dateTimeNow.value,
            conferenceCallDetails.value.StartTimeUtc,
            conferenceCallDetails.value.EnableLiveStream,
            isParticipantAccessRevoked.value,
        );
        onStatusChanged(
            conferenceCallStatus.value,
            conferenceCallDetails.value.EnableLiveStream,
            userStatus.value,
            null,
            isParticipantAccessRevoked.value,
            !!participants.value.IsPublicParticipant
        );
    }
});

watch(dateTimeNow, (newVal, oldVal) => {
    calculateDateTimeOffsetAndPoll(
        newVal,
        conferenceCallDetails.value.StartTimeUtc,
        conferenceCallDetails.value.EnableLiveStream,
        isParticipantAccessRevoked.value,
    );
});

watch(conferenceCallStatus, (newVal, oldVal) => {
    onStatusChanged(
        newVal,
        conferenceCallDetails.value.EnableLiveStream,
        userStatus.value,
        null,
        isParticipantAccessRevoked.value,
        !!participants.value.IsPublicParticipant
    );

    if (newVal.Status && newVal.Status !== oldVal.Status) {
        onConferenceStatusUpdate(newVal);
    }
});

watch(presentation, (newVal, oldVal) => {
    if (conferenceCallDetails.value.PresId &&
        (conferenceCallDetails.value.Status !== Constants.ConferenceStatus.Ended ||
            (conferenceCallDetails.value.Status === Constants.ConferenceStatus.Ended && isPhoneBasedReplayEnabled(conferenceCallDetails.value.ReplayType))) &&
        (!isParticipantAccessRevoked.value || (isParticipantAccessRevoked.value && conferenceCallDetails.value.PublicCallEndTimeUtc && participants.value.IsPublicParticipant))
    ) {
        fetchAccessCodes({
            presId: conferenceCallDetails.value.PresId,
            confId: conferenceCallDetails.value.Id,
            replay: conferenceCallDetails.value.Status === Constants.ConferenceStatus.Ended
        });
    }
}, { immediate: true });

watch(isParticipantAccessRevoked, (newVal, oldVal) => {
    if (newVal) {
        onAccessRevoked();
    }
});

watch(userStatus, (newVal, oldVal) => {
    onUserStatusChange(newVal);
});

watch(redirectVWToPresentationViewer, (newVal) => {
    if (newVal) {
        redirectToPresentationViewer(conferenceCallDetails.value.PresId);
    }
});
// Lifecycle hooks
onBeforeMount(() => {
    initializeComponent();
});

onMounted(() => {
    if (redirectVWToPresentationViewer.value) {
        redirectToPresentationViewer(conferenceCallDetails.value.PresId);
    }
});

onBeforeUnmount(() => {
    resetConferenceStatus();
});

// Router guard
useBeforeRouteLeave((to, from, next) => {
    stopConferenceCallStatusPolling();
    stopUserStatusPolling();
    stopParticipantPolling();
    next();
});
</script>

<style lang="stylus" scoped>
@import '~@/shared/styles/common.scss';
@import '~@/shared/styles/HeroContent.scss';

.enter-event-button {
    background: #285298;
    padding: 12px 0px;
    color: #fff;
    font-size: 15px;
    outline: none;
    border: 1px solid #285298;
    border-radius: 4px;
    position: relative;
    cursor: pointer;
    margin-bottom: 30px;
    width: 50%;
}

.registered-image {
    height: 24px;
    width: 24px;
    margin: auto;
}

.stream-not-started-icon {
    height: 26px !important;
    width: 26px !important;
    display: inline-block !important;
    vertical-align: middle;
}

.loading-state-icon {
    height: 26px !important;
    width: 30px !important;
    display: inline-block !important;
    vertical-align: middle;
}

.stream-mute-icon {
    height: 22px;
    width: 21px;
    display: inline-block;
    vertical-align: middle;
}

.live-dot {
    width: 10px;
    height: 10px;
    border-radius: 5px;
    background-color: green;
    display: inline-block;
    margin-right: 10px;
}

.stream-message {
    color: #43425D;
    padding: 0% 5% 0;
    position: absolute;
    bottom: 200px;
    font-size: 15px;
    line-height: normal !important;
    text-align: left;

    p {
        padding-bottom: 1px;
    }
}

.stream-image {
    display: inline-block;
    width: 150px;
    position: absolute;
    height: 40%;
    opacity: 0.05;
    margin: auto;
    left: 0;
    right: 0;
}

.stream-content {
    min-height: 300px;

    p {
        line-height: inherit !important;
        margin: 0 !important;
    }
}

.global-dial-in-link {
    text-align: center;
    color: #285298;
    font-size: 12px;
    cursor: pointer;
    display: inline;
    text-decoration: underline;
}

.global-dial-in-image {
    height: 14px !important;
    width: 14px !important;
    display: inline-block !important;
    vertical-align: middle;
}

.custom-value,
.custom-label {
    line-height: .7;
    font-size: 1.125rem;

    @media screen and (max-width: 767px) {
        line-height: 1.1;
    }
}

.custom-label {
    font-family: sans-pro-bold;
    margin-right: 5px;
    color: #000;
    text-decoration: none;
}

.dial-in-info-container {
    flex-direction: column;
    padding: 18px 18px 18px;
    box-sizing: border-box;
    display: block;
    min-height: 120px;
    color: #43425D;
}

.add-to-calendar {
    border: none;
    background: none;
    color: #285298;
    font-size: 0.8375rem;
    padding: 0;
    line-height: 1;
    height: 18px;
    cursor: pointer;
    text-decoration: underline;

    &:focus {
        outline: none;
    }
}

.add-calendar-img {
    height: 18px !important;
    width: 18px !important;
    display: inline-block !important;
    vertical-align: middle;
    color: #285298;
}

.custom-content {
    min-height: 450px;
    max-height: 450px;
    flex: none;
    padding: 25px 0 20px 0;
    overflow: visible;
    position: relative;
    box-shadow: 0px 0px 7px 1px rgba(0, 0, 0, 0.1);
    margin-bottom: 10px;

    @media (min-width: 992px) and (max-width : 1320px) {
        min-height: 435px;
        max-height: 435px;
    }
}

.headest-icon {
    height: 21px !important;
    width: 22px !important;
    display: inline-block !important;
    vertical-align: bottom;
    margin-right: 5px;
}

.tabs-button {
    padding: 12px 0px;
    background: '#fff';
    color: '#285298';
    font-size: 15px;
    outline: none;
    border: 1px solid #285298;
    border-radius: 4px;
    position: relative;
    cursor: pointer;

    &:after {
        top: 100%;
        left: 50%;
        border: 'none';
        content: " ";
        height: 0;
        width: 0;
        position: absolute;
        pointer-events: none;
        border-color: rgba(136, 183, 213, 0);
        border-top-color: #285298;
        border-width: 10px;
        margin-left: -30px;
    }
}

.streaming-tab {
    float: 'none';
    width: '45%';
}

.dial-in-tab {
    float: 'right';
    width: '50%';
}

.active-button {
    background: '#285298';
    color: '#fff';

    &:after {
        border: 'solid transparent';
    }
}

.revoke-icon {
    height: 16.67px !important;
    width: 15px !important;
    vertical-align: text-bottom !important;
    margin-right: 8px;
}

.container {
    position: relative;
    padding-bottom: 20px;

    @media screen and (min-width: 991px) {
        margin: 0 auto;
        height: inherit;
    }

    .sub-container {
        height: 100%;
    }
}

.sub-heading {
    line-height: 1.3;
    color: #43425D;
}

.small {
    font-size: 0.9375rem;
}

.footer {
    @extend .small;
    line-height: 1.3;
}

.flex {
    display: flex;
}

.dial-in-information {
    height: 30px;
    align-items: center;
    justify-content: center;
}

.conference-ended-content {
    justify-content: center;
}

.no-margin {
    margin: 0;
}

.dial-in-information-container {
    align-self: center;
}

.gutter-bottom-20 {
    margin-bottom: 20px;
}

.gutter-top-20 {
    margin-top: 20px;
}

.gutter-bottom-30 {
    margin-bottom: 30px;
}

.gutter-bottom-25 {
    margin-bottom: 25px;
}

.stream-in-information {
    height: 25px;
    align-items: center;
    margin: auto;
    width: 95%;
    padding: 0px 15px;
}

.mute-off-div-style {
    background-color: #F4F6F9;
    height: 30%;
    margin-top: 10%;
}

.stream-not-started-icon-style {
    margin-top: 12%;
    padding-bottom: 3%;
}

.footer-style {
    opacity: 0.1;
}

.mute-off-icon-style {
    margin-top: 3%;
}

.flex-wrap-mobile {
    @media screen and (max-width: 992px) {
        height: 45px;
        flex-direction: column;
        margin: 4px 0;

        .number {
            line-height: 1.4;
        }
    }
}

.tab-container {
    padding: 20px 0px 20px 0px;
}

.change-background {
    background: #ffffff;
    box-shadow: 0px 0px 10px 1px #d2d2d2;
    height: 77%;
    padding: 0px 0 20px 0;

    @media screen and (max-width: 768px) {
        min-height: 77%;
    }
}

.white-color {
    color: #fff;
}

.stream-title {
    font-size: 24px;
    color: #285298;
    font-weight: 600;
    text-align: left;
    padding: 5% 5% 0;
}

.stream-status {
    text-align: left;
    padding: 4% 5% 0;
}

.mute-unmute {
    width: 100%;
    height: 95px;
    background: #285298;
    font-size: 18px;
    outline: none;
    position: absolute;
    left: 0;
    border: 1px solid #285298;
    bottom: 90px;
    z-index: 1;

    p {
        color: #fff;
        line-height: normal !important;
        margin: 0 !important;

        span {
            cursor: pointer;
            font-size: 13px;
        }
    }

    .mute-unmute-btn {
        width: 50px;
        height: 50px;
        background: white;
        display: inline-block;
        border-radius: 50%;
        line-height: 50px;
    }

    .mute-unmute-label {
        margin-left: 8px;
        width: 180px;
        display: inline-block;

        span {
            float: left;
        }
    }

    .ja-mute-unmute-label {
        vertical-align: middle !important;
    }

    .normal-mute-unmute-label {
        vertical-align: sub !important;
    }
}

.overlay-class-for-error {
    position: absolute;
    width: 100%;
    z-index: 9997;
    background: rgba(255, 255, 255, 0.95);
    border-radius: 10px;
    top: 0;
    bottom: 0;
}

.bolder {
    font-weight: 700;
}

.access-revoke-msg {
    width: 60%;
    display: inline-block;
}

.increase-height {
    @media screen and (max-width: 992px) {
        min-height: 440px;
        max-height: 440px;
    }
}

.videoWebinarHeader {
    font-weight: 600;
    font-size: 18px;
}

.videoWebinarDetails {
    font-size: 13px;
    padding: 25px 30px;
}

.callConfirmationContainer {
    width: 70%;

    #enterEventButton {
        background: var(--primary-color);
    }

    @media screen and (max-width: 768px) {
        width: auto;
    }
}
</style>
